import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import useAudit from './useAudit'
import { Person, Reason, LoadState, Pagination } from './parts'
import { RatingStar } from './PublicRatings'
import './tournamentFeedback.css'

// The tournament feedback of one event, in the Match reviews tab (sportya-api docs/tournament-feedback.md, „Auditul pentru CP”).
// Themes and reasons come from the catalog the API sends with the rows, in the CP's language.

const LANGUAGES = ['en', 'ro', 'bg', 'hu', 'cz']
export const feedbackLanguage = language => LANGUAGES.includes(language) ? language : 'en'
const ORGANIZER = 'generalNote'

// An approved text with „★” keeps its wording; CP draws the DS star instead of the character (tasks/lessons.md, 2026-10-21).
export function StarText({ text, t }) {
  const parts = text.split('★')
  return parts.map((part, index) => <React.Fragment key={index}>{part}{index < parts.length - 1 && <span className="cp-feedback-star">
    <RatingStar published /><span className="sr-only">{t('feedbackStars')}</span></span>}</React.Fragment>)
}
export function Rating({ value }) {
  return <span className="cp-public-rating"><RatingStar published={value != null} /><strong>{value ?? '—'}</strong></span>
}
const Missing = () => <span className="text-muted">—</span>

export function catalogIndex(catalog) {
  const themes = new Map((catalog?.themes || []).map(theme => [theme.code, { ...theme, reasonMap: new Map((theme.reasons || []).map(reason => [reason.code, reason])) }]))
  return { themes, theme: code => themes.get(code) }
}
export function ThemeTitle({ theme, code, t }) {
  return <>{theme?.type === ORGANIZER ? t('feedbackOrganizer') : theme?.title || code}{theme?.status === 'inactive' && <small className="text-muted"> {t('feedbackDeactivated')}</small>}</>
}
function ReasonName({ theme, code, t }) {
  const reason = theme?.reasonMap.get(code)
  return <>{reason?.text || code}{reason?.status === 'inactive' && <small className="text-muted"> {t('feedbackDeactivated')}</small>}</>
}
// Columns: every active theme, plus an inactive one while the listed answers still use it; the Organizer first, as in the catalog.
export function themeColumns(index, answers) {
  const used = code => answers.some(answer => answer.ratings?.[code] != null || answer.reasons?.[code]?.length)
  return [...index.themes.values()].filter(theme => theme.status === 'active' || used(theme.code))
}

const hidden = t => <span className="text-muted">{t('feedbackHidden')}</span>
function ReasonList({ theme, answer, deleted, struck, t }) {
  const reasons = answer.reasons?.[theme.code] || []
  const Text = struck ? 's' : React.Fragment
  return reasons.length > 0 && <ul className={'list-unstyled small mb-0' + (struck ? ' text-muted' : ' mt-1')}>{reasons.map(code => <li key={code}><Text>
    <ReasonName theme={theme} code={code} t={t} />{code === 'other' && <>: {deleted ? hidden(t) : `„${answer.otherTexts?.[theme.code] ?? ''}”`}</>}
  </Text></li>)}</ul>
}
// A theme's cell: the stars, the reasons chosen and the „Something else” text. The Organizer has no stars; its reasons
// stop counting when the author's general note is now 4–5 and are struck through until the author's next save.
export function ThemeCell({ theme, answer, deleted, notCounted, generalNote, t }) {
  const organizer = theme.type === ORGANIZER
  const rating = answer.ratings?.[theme.code]
  if (organizer) {
    if (!answer.reasons?.[theme.code]?.length) return <td><Missing /></td>
    return <td className="cp-feedback-cell"><ReasonList theme={theme} answer={answer} deleted={deleted} struck={notCounted} t={t} />
      {notCounted && <small className="d-block text-muted"><StarText text={t('feedbackNotCounted', { rating: generalNote })} t={t} /></small>}</td>
  }
  return <td className="cp-feedback-cell">{rating != null ? <Rating value={rating} /> : <Missing />}<ReasonList theme={theme} answer={answer} deleted={deleted} t={t} /></td>
}
export function FeedbackAuthor({ row, t }) {
  return row.authorDeleted ? <>{t('feedbackDeletedAccount', { id: row.authorId })}</> : <Person person={row.author} id={row.authorId} />
}
export function FeedbackMessage({ message, deleted, t }) {
  if (deleted) return <span className="text-muted">{t('feedbackMessageHidden')}</span>
  return message ? <>{message}</> : <Missing />
}
const dateTime = value => value ? new Date(value).toLocaleString() : '—'

function Summary({ summary, index, t }) {
  return <>
    <div className="table-responsive"><table className="table table-sm mb-1" aria-label={t('feedbackByTheme')}>
      <thead><tr>{['feedbackTheme', 'feedbackMean', 'feedbackAnswers', 'feedbackReasonsChosen'].map(key => <th scope="col" key={key}>{t(key)}</th>)}</tr></thead>
      <tbody>{summary.themes.map(item => { const theme = index.theme(item.code); return <tr key={item.code}>
        <td><ThemeTitle theme={theme || item} code={item.code} t={t} /></td>
        <td>{item.type === ORGANIZER ? <span className="text-muted">{t('feedbackNoStars')}</span> : <Rating value={Number.isFinite(item.mean) ? item.mean.toFixed(1) : null} />}</td>
        <td>{item.answers}</td>
        <td>{item.reasons?.length ? item.reasons.map((reason, position) => <React.Fragment key={reason.code}>{position > 0 && ' · '}
          <span className="text-nowrap"><ReasonName theme={theme} code={reason.code} t={t} /> {reason.count}</span></React.Fragment>) : <Missing />}</td>
      </tr> })}</tbody>
    </table></div>
    <p className="small text-muted"><StarText text={t('feedbackExplanation')} t={t} /></p>
  </>
}

function Authors({ data, index, onDetail, t }) {
  const columns = themeColumns(index, data.results)
  return <div className="table-responsive"><table className="table table-striped" aria-label={t('feedbackByAuthor')}>
    <thead><tr>
      <th scope="col">{t('author')}</th>
      {columns.map(theme => <th scope="col" key={theme.code}><ThemeTitle theme={theme} code={theme.code} t={t} /></th>)}
      <th scope="col">{t('feedbackMessage')}</th><th scope="col">{t('feedbackLastSaved')}</th><th scope="col">{t('audit')}</th>
    </tr></thead>
    <tbody>{data.results.map(row => <tr key={row.id}>
      <td><FeedbackAuthor row={row} t={t} />{row.eligibility !== 'active' && <small className="d-block text-muted">{t('inactive')}{row.reason && <> · <Reason reason={row.reason} t={t} /></>}</small>}</td>
      {columns.map(theme => <ThemeCell key={theme.code} theme={theme} answer={row} deleted={row.authorDeleted}
        notCounted={!row.organizerReasonsCounted} generalNote={row.generalNote} t={t} />)}
      <td className="cp-feedback-message"><FeedbackMessage message={row.message} deleted={row.authorDeleted} t={t} /></td>
      <td>{dateTime(row.savedAt)}</td>
      <td><button type="button" className="btn btn-link p-0" onClick={() => onDetail(row)}>{t('viewAudit')}</button></td>
    </tr>)}</tbody>
  </table></div>
}

// The section reads its own route only while it is open, with the tab's author search and event filters and its own pages.
export default function TournamentFeedbackSection({ eventId, open, onToggle, filters, competitionIds, onDetail }) {
  const { t, i18n } = useTranslation('reviewAudit')
  const params = new URLSearchParams()
  if (filters.author) params.set('author', filters.author)
  params.set('competitionLevel', filters.competitionLevel)
  params.set('gameType', filters.gameType)
  if (competitionIds) params.set('competitionIds', competitionIds.join(','))
  const selection = params.toString()
  const [paging, setPaging] = useState({ selection, page: 1 })
  const page = paging.selection === selection ? paging.page : 1
  params.set('page', page)
  params.set('limit', 20)
  params.set('language', feedbackLanguage(i18n.language))
  const state = useAudit(open ? `/admin/events/${eventId}/tournament-feedback?${params}` : null)
  const { data } = state
  const index = catalogIndex(data?.catalog)
  return <details className="cp-review-section border rounded p-3 mb-3" open={open} aria-label={t('tournament-feedback')}>
    <summary onClick={event => { event.preventDefault(); onToggle() }}><strong>{t('tournament-feedback')}</strong>
      {data?.summary && <strong className="float-right ml-3">{t('feedbackRespondents', { count: data.summary.respondents })}</strong>}
      <small className="d-block text-muted">{t('tournament-feedback-hint')}{data?.summary && ' · ' + t('counts', { active: data.summary.activeCount, total: data.summary.total })}</small>
    </summary>
    {open && <div className="pt-3">
      <LoadState state={state} t={t} />
      {data && <>
        {data.summary?.total > 0 && <Summary summary={data.summary} index={index} t={t} />}
        {data.results?.length ? <Authors data={data} index={index} onDetail={onDetail} t={t} /> : <p>{t('empty')}</p>}
        <Pagination data={data} onPage={next => setPaging({ selection, page: next })} t={t} />
      </>}
    </div>}
  </details>
}
